// Microsoft SSO — server-side flow.
//
// The backend owns the OAuth exchange: Azure's redirect_uri points at
// `GET /api/auth/{provider}/callback` on the backend, which swaps the `code`
// itself and then redirects the browser to `{FRONTEND_APP_URL}/` with exactly
// ONE of these query parameters (never both):
//
//   success -> /?sso_ticket=<40 chars [A-Za-z0-9]>
//   failure -> /?sso_error=<code from SsoErrorCode>
//
// Kept as a leaf module (no imports) so the popup bridge, the bootstrap util and
// the Login page can all pull it without dragging in a wider graph.

/** Query parameter carrying the one-time exchange ticket on success. */
export const SSO_TICKET_PARAM = 'sso_ticket';

/** Query parameter carrying the failure code. */
export const SSO_ERROR_PARAM = 'sso_error';

/** Stable failure codes emitted by the backend redirect. */
export enum SsoErrorCode {
  /** `state` expired (>10 min) or was replayed. */
  InvalidState = 'invalid_state',
  /**
   * THREE backend outcomes, deliberately indistinguishable: the email domain
   * is not whitelisted, the account is disabled, or the CRM has no account for
   * that address at all. The last one is the most common, and this comment used
   * to name only the first two.
   */
  AccessDenied = 'access_denied',
  /**
   * NOT CURRENTLY EMITTED BY THE BACKEND. "No CRM user with that email" is
   * refused with 403 and arrives as AccessDenied instead; every 404 in the
   * backend lives in the admin link-creation surface, which never redirects
   * here. Kept — enum value, message and the tests that use them — because
   * surfacing the distinction later is a live option.
   */
  AccountNotFound = 'account_not_found',
  /** Internal backend failure (Redis/MySQL/logger — not a Microsoft problem). */
  ServerError = 'server_error',
  /**
   * Everything else — deliberately broad on the backend, and the code the user
   * actually hits in three very different situations:
   *  - the code-for-token exchange against Microsoft failed;
   *  - Microsoft/Entra is unreachable (network failure or 5xx — the backend
   *    catches every Throwable into the same 401);
   *  - the user CANCELLED or DENIED CONSENT on Microsoft's page. The backend
   *    never reads Microsoft's `error` query param: a cancelled sign-in comes
   *    back with an empty `code`, fails the exchange, and lands here.
   * One copy therefore has to cover all three until the backend introduces
   * distinct codes (this mapper falls back gracefully for any new one).
   */
  AuthFailed = 'auth_failed'
}

/**
 * User-facing text per failure code. English to match the rest of the auth
 * screens ('Member login', 'Sign in with Microsoft', ...).
 */
export const SSO_ERROR_MESSAGES: Record<SsoErrorCode, string> = {
  [SsoErrorCode.InvalidState]: 'Your sign-in session has expired. Please try again.',
  [SsoErrorCode.AccessDenied]: 'Access denied. Please contact your administrator.',
  [SsoErrorCode.AccountNotFound]: 'Account not found. Ask your administrator to create it.',
  [SsoErrorCode.ServerError]: 'Something went wrong. Please try again later.',
  [SsoErrorCode.AuthFailed]: 'Could not sign you in with Microsoft.'
};

/**
 * Shown when the one-time ticket is replayed, has expired (it lives 60 seconds)
 * or is malformed — the backend answers `POST /api/auth/sso/exchange` with 401
 * `{ "message": ["Invalid or expired ticket."], "data": [] }` in all three
 * cases (the envelope always wraps `message` in an array of strings).
 */
export const SSO_EXCHANGE_FAILED_MESSAGE = 'Your sign-in link is no longer valid. Please sign in again.';

/**
 * Maps a raw `sso_error` value to its message. An unrecognised code (backend
 * added one we don't know yet) falls back to the generic `auth_failed` text
 * rather than surfacing the raw code to the user.
 *
 * Own-property check, not a bare index: the code comes straight from a URL
 * query parameter, and a plain-object lookup would resolve prototype keys —
 * `/?sso_error=toString` would return a function, which then crashes
 * history.replace (functions are not structured-cloneable) or React rendering.
 */
export const getSsoErrorMessage = (code: string | null | undefined): string =>
  code && Object.prototype.hasOwnProperty.call(SSO_ERROR_MESSAGES, code)
    ? SSO_ERROR_MESSAGES[code as SsoErrorCode]
    : SSO_ERROR_MESSAGES[SsoErrorCode.AuthFailed];

/** `postMessage` envelope the popup sends to its opener with the redirect result. */
export const SSO_MESSAGE_SOURCE = 'vfm-sso';

/**
 * Acknowledgement the opener sends back. The popup closes itself ONLY after this
 * arrives, which is what makes the handshake safe: a window that merely happens
 * to have a `window.opener` (opened via target="_blank" without rel="noopener")
 * gets no acknowledgement, so it never closes itself — it finishes the sign-in
 * in place instead.
 */
export const SSO_ACK_MESSAGE_SOURCE = 'vfm-sso-ack';

/**
 * How long the popup waits for the acknowledgement before deciding nobody is
 * listening and completing the exchange itself. Well inside the ticket's
 * 60-second lifetime.
 */
export const SSO_ACK_TIMEOUT_MS = 1500;

/**
 * localStorage key prefix under which a window claims a ticket before
 * exchanging it (see claimSsoTicket in ssoPopupBridge). Guards the ack-timeout
 * race: a throttled opener can process the popup's message AFTER the popup gave
 * up waiting, and without the claim both windows would spend the single-use
 * ticket.
 */
export const SSO_TICKET_CLAIM_PREFIX = 'vfm-sso-claim:';

/** Claims older than the ticket's own 60-second lifetime are garbage. */
export const SSO_TICKET_CLAIM_TTL_MS = 60_000;

/**
 * sessionStorage key marking that THIS browsing context (or its opener — a
 * popup inherits a copy of the opener's sessionStorage) deliberately started
 * an SSO sign-in. The same-tab bootstrap refuses to exchange a ticket that
 * arrives without the marker: a `/?sso_ticket=...` URL opened cold (a link
 * someone was sent, a bookmark) must not silently sign the visitor into
 * whatever account the ticket belongs to (login CSRF).
 */
export const SSO_ATTEMPT_MARKER_KEY = 'vfm-sso-attempt';

/**
 * How long an acknowledged popup waits after its own window.close() before
 * concluding the browser refused the scripted close (it is only honoured for
 * script-opened windows) and rendering the app anyway. Without this grace
 * period a window that merely HAS an opener — target="_blank" without
 * rel="noopener" — would stay a permanently blank document.
 */
export const SSO_CLOSE_WATCHDOG_MS = 1000;

/** Window name of the SSO popup — reused so a second click retargets the same window. */
export const SSO_POPUP_NAME = 'vfm-ms-sso';

/** Popup geometry — roughly the size of Microsoft's own sign-in dialog. */
export const SSO_POPUP_FEATURES = 'width=520,height=680';

/** How often the opener checks whether the user closed the popup by hand. */
export const SSO_POPUP_POLL_MS = 500;

/** Provider slug used in the backend SSO routes. */
export const SSO_PROVIDER_MICROSOFT = 'microsoft';
